/**
 * Copy de SEO aprovada (title e meta description) por página.
 * Fonte única dos textos exatos do brief de SEO: nunca derivar a
 * description cortando bio ou texto da página. Os títulos aqui NÃO
 * incluem o sufixo " | Emera" — o SEOHead já o acrescenta.
 */

export interface SeoCopy {
  title: string;
  description: string;
}

export const homeSeo: SeoCopy = {
  title: "Cursos de arbitragem para advogados",
  description:
    "Escola da Arbitralis SA, câmara com +30.000 casos resolvidos. Cursos online de arbitragem, IA no Direito e ECA Digital com quem atua em casos reais.",
};

export const coursesListSeo: SeoCopy = {
  title: "Cursos jurídicos online: arbitragem, IA e ECA",
  description:
    "Cursos online da Emera: Arbitragem Aplicada, masterclasses de arbitragem imobiliária e de IA no dia a dia jurídico e aulas abertas sobre arbitragem e ECA.",
};

export const eventsListSeo: SeoCopy = {
  title: "Eventos e lives sobre arbitragem e Direito",
  description:
    "Agenda de lives e encontros da Emera sobre arbitragem, IA no Direito e ECA Digital, com professores que atuam na Arbitralis SA e no mercado jurídico.",
};

export const instructorsListSeo: SeoCopy = {
  title: "Professores de arbitragem, IA e ECA Digital",
  description:
    "Conheça os formadores da Emera: advogados, árbitros e especialistas que atuam na Arbitralis SA e no mercado, em arbitragem, imobiliário, IA e dados.",
};

export const materialsListSeo: SeoCopy = {
  title: "E-books jurídicos gratuitos sobre arbitragem e IA",
  description:
    "Cinco e-books gratuitos da Emera: prática da arbitragem, arbitragem imobiliária, grandes litigantes do CNJ, ECA Digital e guia de bolso de IA no Direito.",
};

/** Cursos e páginas de venda, por slug. */
export const courseSeo: Record<string, SeoCopy> = {
  "arbitragem-no-mercado-imobiliario": {
    title: "Arbitragem imobiliária: masterclass com Eliza Novaes",
    description:
      "Masterclass de 1 hora, em 5 módulos, com Eliza Novaes: como funciona a arbitragem em contratos imobiliários e como redigir a cláusula compromissória.",
  },
  "uso-de-ia-no-dia-a-dia-juridico": {
    title: "Curso de IA para advogados: masterclass prática",
    description:
      "Masterclass de 1 hora com Thiago Padovani: revisão de contrato, pesquisa jurídica e agente redator nas versões gratuitas das ferramentas, com sigilo.",
  },
  "conceitos-e-fundamentos": {
    title: "Arbitragem e mediação: conceitos e fundamentos",
    description:
      "Aula aberta de 1 hora com Daniel Gontijo: diferenças entre arbitragem, mediação e conciliação, Lei 9.307/96 e como evitar a cláusula arbitral vazia.",
  },
  "eca-digital-aplicado-a-produtos-e-negocios-digitais": {
    title: "ECA Digital para empresas: aula sobre adequação",
    description:
      "Aula aberta de 1 hora com Flávia Braz de Castro sobre a Lei 15.211/2025: escopo, aferição de idade e plano de adequação para produtos e negócios digitais.",
  },
};

/** E-books e materiais, por slug. */
export const materialSeo: Record<string, SeoCopy> = {
  "guia-arbitragem-2026": {
    title: "Procedimento arbitral passo a passo: e-book grátis",
    description:
      "E-book gratuito com a linha do tempo do procedimento arbitral e os prazos da Lei 9.307/1996, checklists práticos, 13 decisões do STJ e glossário.",
  },
  "grandes-litigantes": {
    title: "Grandes litigantes do CNJ: e-book com dados 2026",
    description:
      "Quem concentra os processos no Brasil, segundo o CNJ (Justiça em Números 2026 e Painel de Grandes Litigantes), quanto custa o contencioso e o que fazer.",
  },
  "eca-digital-na-pratica": {
    title: "Checklist ECA Digital (Lei 15.211/2025): e-book",
    description:
      "Guia gratuito, não o texto da lei: checklist de escopo, obrigações por artigo, sanções do art. 35 e roteiro de adequação em 8 passos para apps e jogos.",
  },
  "checklist-uso-etico-ia": {
    title: "Checklist de uso ético de IA no escritório",
    description:
      "25 itens para conferir antes de usar inteligência artificial com documentos e dados de clientes.",
  },
  "uso-profissional-de-ia-no-direito-guia-de-bolso": {
    title: "Prompts para advogados: Guia de Bolso de IA",
    description:
      "E-book gratuito de Thiago Padovani com prompts prontos para a rotina do escritório, cuidados com sigilo e dados de clientes e um passo a passo de revisão.",
  },
  "arbitragem-imobiliaria-o-novo-padrao": {
    title: "Cláusula arbitral em locação e compra: e-book",
    description:
      "E-book gratuito de Eliza Novaes, OAB/MG 107.556, sobre arbitragem em compra e venda, locação e condomínio, com modelos de cláusula comentados.",
  },
};

/** Formadores, por slug. A description é a bio curta em texto puro. */
export const instructorSeo: Record<string, SeoCopy> = {
  "daniel-gontijo": {
    title: "Daniel Gontijo, fundador da Arbitralis e da Emera",
    description:
      "Daniel Gontijo é advogado, mestre em Direito pela FUMEC e fundador da Arbitralis (2020) e da Emera. Ensina arbitragem a partir da prática de uma câmara.",
  },
  "flavia-braz-de-castro": {
    title: "Flávia Braz de Castro, privacidade e ECA Digital",
    description:
      "Flávia Braz de Castro é advogada em privacidade, proteção de dados e regulação de novas tecnologias, especialista pela PUC Minas. Ensina ECA Digital.",
  },
  "thiago-padovani": {
    title: "Thiago Padovani, tecnologia e IA no jurídico",
    description:
      "Thiago Padovani atua em tecnologia desde 1999 e no setor jurídico desde 2017, com MBA em IA aplicada ao negócio. Ensina IA no dia a dia jurídico.",
  },
  "arthur-thomazi-moreira": {
    title: "Arthur Thomazi Moreira, Direito Imobiliário",
    description:
      "Arthur Thomazi Moreira é advogado especialista em Direito e Negócios Imobiliários, ex-professor de pós da PUC Minas e sócio fundador do C2T Adv.",
  },
  "katia-gontijo": {
    title: "Kátia Gontijo, advogada e professora de Direito",
    description:
      "Kátia Gontijo é advogada, mestre em Administração Acadêmica e especialista em Direito Público. Lecionou na PUC Minas, UNA e UNI-BH e foi jornalista.",
  },
  "bruna-duque": {
    title: "Bruna Duque, advogada e árbitra da Arbitralis",
    description:
      "Bruna Duque é advogada e árbitra da Arbitralis, especialista em Arbitragem, Conciliação e Mediação, com formação em Direito Imobiliário e Condominial.",
  },
  "brenno-luna-carinhanha": {
    title: "Brenno Luna Carinhanha, jurídico da Arbitralis",
    description:
      "Brenno Luna Carinhanha é advogado formado pela PUC Minas, atua na gestão de procedimentos arbitrais na Arbitralis desde 2022 e co-lidera o jurídico.",
  },
  "eliza-novaes": {
    title: "Eliza Novaes, advogada imobiliária e condominial",
    description:
      "Eliza Novaes é advogada há 18 anos na área imobiliária e condominial, preside a Comissão de Direito Imobiliário da OAB/MG e presidiu a AMADI.",
  },
};
